import { PageTitleWithBackButton } from "@/core/components/PageBackButton";
import { PricelistPeriodListContainer } from "@/features/pricelist-period/containers/PricelistPeriodListContainer";
import PricelistProductListContainer from "@/features/pricelist-product/containers/PricelistProductListContainer";
import PricelistSalesChannelListContainer from "@/features/pricelist-sales-channel/containers/PricelistSalesChannelListContainer";
import type { Pricelist } from "@/generated/graphql";
import {
	type AutocompleteFetcher,
	Badge,
	Button,
	Can,
	DialogProvider,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	SectionBuilder,
	type SectionNode,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	useDialogController,
} from "@k3-tech/react-kit";
import { EllipsisIcon, PlusIcon } from "lucide-react";
import PricelistPeriodAutocomplete from "./PricelistPeriodAutocomplete";

export interface PricelistDetailProps {
	data: Partial<Pricelist>;
	onEdit: () => void;
	onDelete: () => Promise<void>;
	onBack: () => void;
	onReload: () => void;
	onAddPeriod: () => void;
	pricelistPeriodAutocompleteFetcher: AutocompleteFetcher;
	selectedPricelistPeriod?: string;
	onSelectPricelistPeriod: (pricelistPeriodID: string) => void;
	activeTab: PricelistDetailTab;
	onChangeTab: (tab: PricelistDetailTab) => void;
}

export const PRICELIST_DETAIL_TABS = [
	"productPrices",
	"periods",
	"channelMarkup",
] as const;

export type PricelistDetailTab = (typeof PRICELIST_DETAIL_TABS)[number];

export const PricelistDetail = ({
	data,
	onEdit,
	onDelete,
	onBack,
	onReload,
	onAddPeriod,
	pricelistPeriodAutocompleteFetcher,
	selectedPricelistPeriod,
	onSelectPricelistPeriod,
	activeTab,
	onChangeTab,
}: PricelistDetailProps) => {
	const { confirm } = useDialogController();

	// Pre-select the first ACTIVE period: an inactive period is never used for
	// pricing, so defaulting to one would show prices no transaction can hit.
	// The dropdown itself still lists every period, so an inactive one stays
	// reachable on purpose. Falls back to the first period when none is active,
	// so the screen still renders something instead of an empty list.
	const pricelistPeriods = data.pricelistPeriods ?? [];
	const defaultPricelistPeriod =
		pricelistPeriods.find((period) => period.isActive) ?? pricelistPeriods[0];

	const sections: SectionNode[] = [
		{
			id: "summary",
			variant: "plain",
			layout: "grid",
			className: "border-gray-200",
			grid: { cols: 1, mdCols: 4, gap: "gap-6" },
			children: [
				{
					key: "header",
					span: { base: 4 },
					content: (
						<div className="flex items-start justify-between gap-4">
							<div>
								<PageTitleWithBackButton
									title={`${data.pricelistCode}/${data.pricelistName}`}
									onBack={onBack}
								/>
								<div className="mt-2">
									<Badge
										variant={data.isActive ? "secondary" : "secondary"}
										className={data.isActive ? "text-green-600" : undefined}
									>
										{data.isActive ? "Active" : "Inactive"}
									</Badge>
								</div>
							</div>
							<div className="flex gap-2">
								<Can permissions="pricelist.edit">
									<Button onClick={onEdit}>Edit</Button>
								</Can>
								<Can permissions="pricelist.delete">
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button variant="outline">
												<EllipsisIcon className="h-4 w-4" />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent>
											<Can permissions="pricelist.delete">
												<DropdownMenuItem
													onSelect={async () => {
														const ok = await confirm({
															title: "Delete?",
															description: "This action cannot be undone.",
															destructive: true,
															confirmText: "Delete",
															cancelText: "Cancel",
														});
														if (!ok) return;
														await onDelete();
													}}
												>
													Delete
												</DropdownMenuItem>
											</Can>
										</DropdownMenuContent>
									</DropdownMenu>
								</Can>
							</div>
						</div>
					),
				},
				{
					key: "code",
					label: "Code",
					content: data.pricelistCode,
					labelLayout: "stacked",
				},
				{
					key: "name",
					label: "Name",
					content: data.pricelistName,
					labelLayout: "stacked",
				},
			],
		},
	];

	return (
		<>
			<SectionBuilder sections={sections} />

			<div className="mb-6" />

			<Tabs
				value={activeTab}
				onValueChange={(value) => onChangeTab(value as PricelistDetailTab)}
			>
				<TabsList>
					<TabsTrigger value="productPrices">Product Prices</TabsTrigger>
					<TabsTrigger value="periods">Periods</TabsTrigger>
					<TabsTrigger value="channelMarkup">Channel Markup</TabsTrigger>
				</TabsList>

				<TabsContent value="productPrices" className="mt-6">
					{/* The table below is scoped to this period, so the picker leads it. */}
					<div className="mb-4">
						<PricelistPeriodAutocomplete
							initialPricelistPeriod={defaultPricelistPeriod}
							autocompleteFetcher={pricelistPeriodAutocompleteFetcher}
							onSelectPricelistPeriod={onSelectPricelistPeriod}
						/>
					</div>
					<PricelistProductListContainer
						pricelistID={data.pricelistId}
						pricelistPeriodID={
							selectedPricelistPeriod ??
							defaultPricelistPeriod?.pricelistPeriodID
						}
					/>
				</TabsContent>
				<TabsContent value="periods" className="mt-6">
					<div className="mb-4 flex justify-end">
						<Can permissions="pricelist.create">
							<Button onClick={onAddPeriod}>
								<PlusIcon className="h-4 w-4" />
								<span>Add Period</span>
							</Button>
						</Can>
					</div>
					<DialogProvider>
						<PricelistPeriodListContainer pricelistID={data.pricelistId} />
					</DialogProvider>
				</TabsContent>

				<TabsContent value="channelMarkup" className="mt-6">
					<p className="mb-3 text-muted-foreground text-sm">
						Percentage added to the selling price for the price sent to each
						channel. Leave a field empty to follow the channel default.
					</p>
					<PricelistSalesChannelListContainer
						pricelistID={data.pricelistId}
						onSaved={onReload}
					/>
				</TabsContent>
			</Tabs>
		</>
	);
};
